import React, { useState, useEffect, useRef, useCallback } from "react"
import { navigate } from "vike/client/router"
import { usePageContext } from "vike-react/usePageContext"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import MainBackground from "@components/MainBackground"
import Value from "aldoo-ra/Value"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import CMSData from "aldoo-ra/CMS/cms-data"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Spinner from "aldoo-ra/Spinner"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import Tabs from "aldoo-ra/Tabs"
import Input from "aldoo-ra/Input"
import { ChangeUsername, UploadUserAvatar } from "aldoo-ra/PlatformAuth/api"
import AddMediaDialog from "aldoo-ra/AddMediaDialog"
import { url as cmsUrl } from "aldoo-ra/CMS/url"
import { getStoreAccount, getInvoices, getPricingData } from "aldoo-ra/Store/api"
import { store } from "aldoo-ra/Store"
import { api } from "aldoo-ra/API"
import Localization from "aldoo-ra/Localization"
import CancellationFlow from "aldoo-ra/Store/CancelFlow"
import PubSub from "pubsub-js"
import { showSuccessToast, TOAST_POSITIONS } from "aldoo-ra/Toast"
import DataTable from "aldoo-ra/DataTable"
import Tooltip from "aldoo-ra/Tooltip"
import moment from "moment"
import {
  UserCircleIcon,
  CreditCardIcon,
  CurrencyDollarIcon,

  CircleStackIcon,
  DocumentTextIcon,
  PlusCircleIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ArrowRightStartOnRectangleIcon,
  WalletIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FolderIcon,
  BookOpenIcon,
  ChartBarIcon,
  SwatchIcon,
  InboxArrowDownIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline"
import UserPaymentMethods from "@components/UserPaymentMethods"
import BillingDetailsCard from "@components/BillingDetailsCard"

import SnapFilesPanel, { SnapUsagePanel } from "@components/SnapFiles"
import LicensesPanel from "./Licenses"
import Downloadable from "aldoo-ra/Downloadables"
import useSnapRealtime from "./useSnapRealtime"
import { KeyIcon } from "@heroicons/react/24/outline"
import { fetchPlanFeatures } from "@components/Site/Pricing/plan-features"


// Map CMS item names to icon components
const ICON_MAP = {
  AccountNavProfile: UserCircleIcon,
  AccountNavYourFiles: FolderIcon,
  AccountNavMyShares: FolderIcon,
  AccountNavUsage: ChartBarIcon,
  AccountNavPlans: CreditCardIcon,

  AccountNavContacts: BookOpenIcon,
  AccountNavInvoices: DocumentTextIcon,
  AccountNavPaymentMethods: WalletIcon,
  AccountNavBilling: WalletIcon,
  AccountNavThemes: SwatchIcon,
  AccountNavDownloads: ArrowDownTrayIcon,
  AccountNavReceiveLinks: InboxArrowDownIcon,
  AccountNavSent: PaperAirplaneIcon,
  AccountNavLogout: ArrowRightStartOnRectangleIcon,
  AccountNavLicenses: KeyIcon,
}

// Extract section ID from item — options.action value (e.g. options.action:"plans")
// or the last path segment of the link URL (e.g. /account/plans → "plans").
function getSectionId(item) {
  if (!item) return null
  const action = item.options?.action
  if (action && action !== "logout") return action
  const url =
    item?.link?.[0]?.url ||
    item?.link?.[0]?.content?.url ||
    item?.content?.link?.[0]?.url ||
    item?.content?.link?.[0]?.content?.url
  if (!url) return null
  const m = String(url).match(/\/account\/([^/?#]+)/)
  return m?.[1] || null
}

// ---------------------------------------------------------------------------
// Downloads Section — downloadable widgets (latest app installers/content)
// ---------------------------------------------------------------------------
function DownloadsSection({ texts }) {
  return (
    <div className="flex flex-col gap-4">
      {/* Latest AldooSnap installer for macOS — the widget resolves the
          newest published version and gates the download by eligibility */}
      <Downloadable id="snap-macos" variant="snap" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Profile Section
// ---------------------------------------------------------------------------
function ProfileSection({ profile, texts }) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(profile?.name || "")
  const [saving, setSaving] = useState(false)
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false)
  const [avatarUploading, setAvatarUploading] = useState(false)

  useEffect(() => {
    setName(profile?.name || "")
  }, [profile?.name])

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed || trimmed === profile?.name) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await ChangeUsername(trimmed)
      await PlatformAuth.refreshProfile()
    } finally {
      setSaving(false)
      setEditing(false)
    }
  }

  const handleCancel = () => {
    setName(profile?.name || "")
    setEditing(false)
  }

  const avatarSrc = profile?.avatar ? cmsUrl(profile.avatar) : null

  return (
    <div className="flex flex-col gap-6">
      {/* Avatar + Name + Email */}
      <div className="flex md:items-center gap-5 paper_30 rounded-2xl p-6">
        {/* Clickable avatar */}
        <button
          onClick={() => !avatarUploading && setAvatarDialogOpen(true)}
          className="relative h-16 shrink-0 group rounded-full focus:outline-none"
          disabled={avatarUploading}
        >
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt=""
              className="w-16 h-16 rounded-full object-cover border border-bordered"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <UserCircleIcon className="w-10 h-10 text-primary/40" />
            </div>
          )}
          {avatarUploading ? (
            <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
              <Spinner className="w-5 h-5 text-white" />
            </div>
          ) : (
            <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center">
              <PencilIcon className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition" />
            </div>
          )}
        </button>

        {/* Name + Email */}
        <div className="flex-1 min-w-0 flex flex-col sm:gap-2">
          {/* Name row */}
          {editing ? (
            <div className="flex flex-wrap md:flex-nowrap items-center gap-2">
              <Input
                variant="pulse_compact"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSave()}
                autoFocus
                className="w-full md:flex-1 md:max-w-sm"
              />

              <div className="flex items-center gap-2 ml-auto md:ml-0">
                <Button
                  variant="icon"
                  onClick={handleSave}
                  disabled={saving}
                  className="disabled:opacity-40"
                >
                  {saving ? <Spinner className="w-5 h-5" /> : <CheckIcon className="h-5 w-5 text-success stroke-2" />}
                </Button>

                <Button
                  variant="icon"
                  onClick={handleCancel}
                  className="group"
                >
                  <XMarkIcon className="h-5 w-5 stroke-2 group-hover:text-text transition-colors" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Typography component="h3" variant="txt_accent" className="text-text truncate">
                {profile?.name || "-"}
              </Typography>

              <Button
                variant="icon"
                onClick={() => setEditing(true)}
              >
                <PencilIcon className="h-4 w-4" />
              </Button>
            </div>
          )}
          {/* Email — absent for accounts identified another way (Setapp),
              where a placeholder dash just reads as missing data */}
          {profile?.email && (
            <span className="text-sm text-text_secondary truncate">
              {profile.email}
            </span>
          )}
        </div>
      </div>

      {/* Avatar picker — crop + upload via the shared add-media dialog */}
      {avatarDialogOpen && (
        <AddMediaDialog
          imagesOnly
          currentImageUrl={avatarSrc}
          uploadFn={async (file, mimeType) => {
            setAvatarUploading(true)
            try {
              return await UploadUserAvatar(file, mimeType)
            } finally {
              setAvatarUploading(false)
            }
          }}
          onUse={async () => {
            setAvatarDialogOpen(false)
            await PlatformAuth.refreshProfile()
          }}
          onCancel={() => setAvatarDialogOpen(false)}
        />
      )}
    </div>
  )
}

// the store's price resolver: a customer reads prices through it, never the
// generic CRUD api (that is the admin's)
const storeAPI = api("store")
const pricingAPI = api("aldoo-transfer/pricing")

// ---------------------------------------------------------------------------
// Plans Section
// ---------------------------------------------------------------------------
function PlansSection({ texts }) {
  const [account, setAccount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [cancelFlowSub, setCancelFlowSub] = useState(null)
  const [thumbnailsByPid, setThumbnailsByPid] = useState({})
  const [priceByPid, setPriceByPid] = useState({})
  const [metaByPid, setMetaByPid] = useState({}) // kept for potential fallback use
  const [planFeaturesByPid, setPlanFeaturesByPid] = useState({})
  // CMS Feature Matrix bullets (components/Site/Pricing/plan-features.js) —
  // covers products (e.g. snap-lifetime-*, snap-pro-*) not in the Transfer
  // feature matrix that planFeaturesByPid is built from.
  const [matrixFeaturesByPid, setMatrixFeaturesByPid] = useState({})
  const { currentLanguage, t } = Localization()

  // Purchase origin helpers. Subscriptions track the platform that bought
  // them ("iOS" / "Web" / etc); Apple's policy is that any change to an
  // Apple-bought subscription has to flow through the App Store, so the
  // web account page can't drive cancel / upgrade in-line for those.
  const isApplePurchase = (sub) => {
    const p = (sub?.platform || sub?.store || "").toString().toLowerCase()
    return p === "ios" || p === "apple"
  }
  const isOnIOSDevice = () => {
    if (typeof navigator === "undefined") return false
    const ua = navigator.userAgent || ""
    return /iPhone|iPad|iPod/i.test(ua)
      || (/Mac/.test(ua) && typeof document !== "undefined" && "ontouchend" in document) // iPad on Safari reports MacIntel
  }

  // Upgrade — iOS-purchased subs: try opening the native app
  // (aldoo-transfer://pricing). On desktop the deep link silently fails
  // and we still navigate to /pricing so the user has a path forward.
  const handleUpgrade = (sub) => {
    if (isApplePurchase(sub)) {
      if (isOnIOSDevice()) {
        // Attempt the custom URL scheme; iOS Safari either jumps to the
        // app or surfaces the universal-link prompt. Either way the
        // window.location assignment doesn't navigate the page.
        try { window.location.href = "aldoo-transfer://pricing" } catch {}
        return
      }
      navigate("/pricing")
      return
    }
    if (sub.pid?.startsWith("transfer-storage-")) {
      const langMatch = window.location.pathname.match(/^\/([a-z]{2})\//)
      const prefix = langMatch ? `/${langMatch[1]}` : ""
      window.location.href = `${prefix}/pricing#storage`
    } else {
      navigate("/pricing")
    }
  }

  // Cancel — iOS-purchased subs must be cancelled in the App Store, so
  // route the click straight to the iTunes subscriptions page. Web-bought
  // subs continue through the standard CancellationFlow modal.
  const handleCancel = (sub) => {
    if (isApplePurchase(sub)) {
      try { window.open("https://apps.apple.com/account/subscriptions", "_blank", "noopener") } catch {}
      return
    }
    setCancelFlowSub(sub)
  }

  const handleReactivate = async (sub) => {
    const pid = sub.product?.pid || sub.pid
    if (!pid) {
      navigate("/pricing")
      return
    }

    try {
      // the product as the web store sells it: its Web store id and price
      const product = await storeAPI({ resolve_price: { pid, platform: "Web" } })
      if (!product || product.error) return navigate("/pricing")

      const primaryModel = product.price_model?.find((m) => m.primary) || product.price_model?.[0]
      if (!primaryModel) return navigate("/pricing")

      store.addToCart({
        product: {
          pid: product.pid,
          product_id: product.product_id,
          store_id: product.store_id,
          name: sub.product?.name || product.name,
          currency: primaryModel.currency || product.currency || "USD",
          price_model: [{ ...primaryModel, primary: true }],
          thumbnail: product.thumbnail,
        },
        proceedToCart: true,
      })
    } catch {
      navigate("/pricing")
    }
  }

  const loadAccount = () =>
    getStoreAccount()
      .then((res) => setAccount(res?.result || res || null))
      .finally(() => setLoading(false))

  // Order matters — shown top-to-bottom on the plan card.
  const PLAN_FEATURE_IDS = ["transfer-volume-30d", "transfer-count-30d", "max-share-retention-days"]

  const featureLabel = (id, param, value) => {
    const unlimited = String(value).toLowerCase() === "unlimited" || param === "Unlimited"
    if (id === "transfer-volume-30d") return unlimited ? texts?.unlimitedTransferVolume : t(texts?.transferVolumeMonthly, { param })
    if (id === "transfer-count-30d") return unlimited ? texts?.unlimitedTransfersPerMonth : t(texts?.transferCountMonthly, { param })
    if (id === "max-share-retention-days") return t(texts?.transferExpiryUpTo, { param })
    return param
  }

  useEffect(() => { loadAccount() }, [])

  useEffect(() => {
    fetchPlanFeatures().then(setMatrixFeaturesByPid).catch(() => {})
  }, [])

  useEffect(() => {
    if (!currentLanguage) return
    Promise.all([pricingAPI({ pricing: true }), getPricingData(currentLanguage)])
      .then(([data, pricingData]) => {
        if (!data?.products) return
        const thumbMap = {}
        const priceMap = {}
        const metaMap = {}
        for (const p of Object.values(data.products)) {
          if (!p.pid) continue
          if (p.thumbnail) thumbMap[p.pid] = p.thumbnail
          if (p.meta) metaMap[p.pid] = p.meta
          if (p.price && data.prices?.[p.price]) {
            const priceObj = data.prices[p.price]
            const model = priceObj.price_model?.find((m) => m.primary) || priceObj.price_model?.[0]
            if (model?.amount != null) {
              const interval = model.phases?.[0]?.interval || "month"
              priceMap[p.pid] = { amount: model.amount, interval }
            }
          }
        }
        setThumbnailsByPid(thumbMap)
        setPriceByPid(priceMap)
        setMetaByPid(metaMap)

        // Build pid (Stripe) → plan MongoDB _id map via storeProduct _id
        const storeIdToPlanId = {}
        for (const plan of (pricingData?.plans || [])) {
          const storeId = plan.product?.item
          if (storeId) storeIdToPlanId[storeId] = plan._id
        }
        const featById = {}
        for (const f of (pricingData?.features || [])) featById[f.id] = f
        const featuresByPid = {}
        for (const [storeMongoId, product] of Object.entries(data.products)) {
          if (!product.pid) continue
          const planId = storeIdToPlanId[storeMongoId]
          if (!planId) continue
          // Build in PLAN_FEATURE_IDS order so the card reads volume → count → expiry.
          featuresByPid[product.pid] = PLAN_FEATURE_IDS
            .map((fid) => {
              const feature = featById[fid]
              if (!feature) return null
              const pv = (feature.plan_values || []).find((pv) => {
                const pvPlan = typeof pv.plan === "string" ? pv.plan : pv.plan?._id
                return pvPlan === planId
              })
              if (!pv || pv.param === "✗") return null
              return { id: fid, label: featureLabel(fid, pv.param, pv.value) }
            })
            .filter(Boolean)
        }
        setPlanFeaturesByPid(featuresByPid)
      })
      .catch(() => {})
  }, [currentLanguage])

  // Refresh after cancellation flow completes
  useEffect(() => {
    const token = PubSub.subscribe("account-update", () => loadAccount())
    return () => PubSub.unsubscribe(token)
  }, [])

  // Realtime: server pushes account.plan_changed after a subscription
  // expiry cascade. Reload the account so plan card / cancel CTA flip
  // without a manual refresh and toast the user.
  useEffect(() => {
    const token = PubSub.subscribe("transfer.account.plan_changed", () => {
      loadAccount()
      try {
        showSuccessToast(
          texts?.backOnFreePlan,
          texts?.subscriptionEndedNote,
          {
            position: TOAST_POSITIONS.TOP_CENTER,
            pauseOnHover: false,
            enableDismissalTracking: false,
            duration: 6000,
          },
        )
      } catch {}
    })
    return () => PubSub.unsubscribe(token)
  }, [])

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-12 text-text_secondary">
        <Spinner className="w-[18px] h-[18px]" />
        {texts?.loading}
      </div>
    )
  }

  // License-seat entitlements: the licensing flow writes a synthetic
  // ProductSubscription (receipt_id "license-activation:<code>") for every
  // attached seat so feature access works. It's not a purchase — seat
  // members must not see a plan card (Cancel would hit Stripe with no
  // subscription id). Their license lives in the Licenses tab.
  const subscriptions = (account?.subscriptions || []).filter(
    (s) => !s.metadata?.receipt_id?.startsWith("license-activation:")
  )
  const allMainSubs = subscriptions.filter((s) => !s.pid?.startsWith("transfer-storage-"))
  const storageSubs = subscriptions.filter((s) => s.pid?.startsWith("transfer-storage-"))

  // When the user owns an active paid plan, hide any canceled paid
  // subs from previous tiers — Apple cancels the old Starter when Pro
  // activates, leaving a canceled Starter row behind that's NOT a real
  // downgrade-in-progress, just upgrade residue. Users were thinking
  // their Pro purchase had failed. Once there's no active paid plan
  // (true downgrade window), the canceled card returns so the user
  // can see the "access until" date.
  const isPaid = (s) => s.pid && s.pid !== "transfer-plan-free"
  const hasActivePaid = allMainSubs.some(
    (s) => isPaid(s) && (s.status === "active" || s.status === "trialing")
  )
  const mainSubs = hasActivePaid
    ? allMainSubs.filter((s) => !(isPaid(s) && s.status === "canceled"))
    : allMainSubs

  if (subscriptions.length === 0) {
    return (
      <div className="bg-paper rounded-2xl border border-bordered p-8 flex flex-col items-center gap-3 text-center">
        <CreditCardIcon className="h-10 w-10 text-text_secondary/40" />
        <Typography variant="txt1_dim" className="my-0">
          {texts?.noActivePlans}
        </Typography>
        <Button
          variant="secondary"
          className="mt-2"
          onClick={() => navigate("/pricing")}
        >
          {texts?.viewPlans}
        </Button>
      </div>
    )
  }

  const renderSub = (sub) => {
    const isActive = sub.status === "active" || sub.status === "trialing"
    const renewDate = sub.current_period_end
      ? new Date(sub.current_period_end * 1000).toLocaleDateString()
      : null
    const pid = sub.product?.pid || sub.pid
    const priceInfo = priceByPid[pid] || null
    const transferFeatures = planFeaturesByPid[pid] || []
    // CMS Feature Matrix rows are { name, value } (e.g. "Max link duration"
    // + "3 days") — compose into the same { id, label } shape the Transfer
    // features already use below.
    const cmsFeatures = (matrixFeaturesByPid[pid] || []).map((f) => ({
      id: f.id,
      label: f.value ? `${f.name} ${f.value}` : f.name,
    }))
    const features = transferFeatures.length > 0 ? transferFeatures : cmsFeatures

    // Hide Upgrade on top-tier subs — Pro is the ceiling so there's nothing
    // to upgrade to. Cancel + Reactivate still surface.
    const showUpgrade = isActive && !(pid === "transfer-plan-pro" || pid?.includes?.("-pro"))
    const showCancel = isActive && !sub.cancel_at_period_end && sub.product?.amount > 0
    const showReactivate = sub.status === "canceled"

    return (
      <div
        key={sub.id || sub._id}
        className="w-full max-w-sm paper_30 rounded-3xl p-6 flex flex-col"
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <Typography
            component="h4"
            variant="txt_accent"
            className="text-text"
          >
            {sub.product?.name || sub.product_name || sub.plan_name || texts?.subscription}
          </Typography>

          <span
            className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
              isActive
                ? "bg-success text-white"
                : sub.status === "canceled"
                  ? "bg-error text-white"
                  : "bg-warning text-white"
            }`}
          >
            {sub.status?.charAt(0).toUpperCase() + sub.status?.slice(1)}
          </span>
        </div>

        {(() => {
          // Per-seat subscriptions: show seats × unit and the real total.
          // price_model.amount on the subscription is the charged total;
          // fall back to the catalog price for legacy/simple subs.
          const seats = sub.quantity || 1
          const total = sub.price_model?.amount ?? priceInfo?.amount
          if (total == null) return null
          const durationDays = sub.price_model?.duration || 0
          const isAnnual = durationDays >= 360 || priceInfo?.interval === "year"
          const perMonth = Math.round((isAnnual ? total / 12 : total) * 100) / 100
          const unit = Math.round((total / seats) * 100) / 100
          return (
            <Typography
              variant="txt2_dim"
              className="block mb-4"
            >
              ${perMonth} / {texts?.perMonth}
              {isAnnual && ` · $${total} ${texts?.billedAnnually}`}
              {seats > 1 && (
                <>
                  {" · "}
                  {seats} {texts?.seat_plural} × ${isAnnual ? Math.round((unit / 12) * 100) / 100 : unit}/{texts?.perMonth}
                </>
              )}
            </Typography>
          )
        })()}

        {features.length > 0 && (
          <ul className="flex-1 mb-8 space-y-3">
            {features.map((f) => (
              <li key={f.id} className="flex items-start gap-2.5 text-sm text-text">
                <CheckIcon className="h-5 w-5 text-primary shrink-0" strokeWidth={2.5} />
                <span>{f.label}</span>
              </li>
            ))}
          </ul>
        )}

        {renewDate && (
          <Typography
            variant="txt2_dim"
            className="block mb-4"
          >
            {sub.cancel_at_period_end
              ? `${texts?.retainAccess} ${renewDate}`
              : `${texts?.renewsOn} ${renewDate}`}
          </Typography>
        )}

        {(showUpgrade || showCancel || showReactivate) && (
          <div className="flex gap-2 mt-auto">
            {showUpgrade && (
              <Button
                variant="primary"
                className="flex-1"
                onClick={() => handleUpgrade(sub)}
              >
                {texts?.upgradePlan}
              </Button>
            )}
            {showCancel && (
              <Button
                variant="secondary_red"
                className="flex-1"
                onClick={() => handleCancel(sub)}
              >
                {texts?.cancelPlan}
              </Button>
            )}
            {showReactivate && (
              <Button
                variant="primary"
                className="flex-1"
                onClick={() => handleReactivate(sub)}
              >
                {texts?.reactivate}
              </Button>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {mainSubs.length > 0 && (
        <div className="flex flex-wrap justify-center gap-4">
          {mainSubs.map(renderSub)}
        </div>
      )}

      {storageSubs.length > 0 && (
        <div className="flex flex-col gap-4">
          <h3 className="text-xs font-semibold text-text_secondary uppercase tracking-wide">
            {texts?.storageAddons}
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {storageSubs.map(renderSub)}
          </div>
        </div>
      )}

      <CancellationFlow
        isOpen={!!cancelFlowSub}
        subscription={cancelFlowSub}
        onClose={() => setCancelFlowSub(null)}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Billing Section — Payment methods + Invoices behind tabs
// ---------------------------------------------------------------------------
function BillingSection({ texts, initialTab = "payment-methods" }) {
  const valid = ["payment-methods", "invoices", "billing-info"]
  const resolveTab = (v) => (valid.includes(v) ? v : "payment-methods")
  const [tab, setTab] = useState(resolveTab(initialTab))

  // Sync tab if URL changes (browser back/forward).
  useEffect(() => {
    const next = resolveTab(initialTab)
    setTab((t) => (t === next ? t : next))
  }, [initialTab])

  // Push URL when tab changes (locale-aware), keeping the tab in browser history.
  const handleTabChange = (next) => {
    setTab(next)
    if (typeof window === "undefined") return
    const m = window.location.pathname.match(/^(\/[a-z]{2})?\/account\/billing(?:\/[^/?#]+)?/)
    if (!m) return
    const langPrefix = m[1] || ""
    const target = next === "payment-methods"
      ? `${langPrefix}/account/billing`
      : `${langPrefix}/account/billing/${next}`
    if (window.location.pathname !== target) navigate(target)
  }

  const tabs = [
    { value: "payment-methods", label: texts?.paymentMethods, className: "whitespace-nowrap shrink-0" },
    { value: "invoices", label: texts?.invoices, className: "whitespace-nowrap shrink-0" },
    { value: "billing-info", label: texts?.billingInformation, className: "whitespace-nowrap shrink-0" },
  ]

  const tabScrollRef = useRef(null)
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false)
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false)
  const checkTabScroll = useCallback(() => {
    const el = tabScrollRef.current
    if (!el) return
    setCanScrollTabsLeft(el.scrollLeft > 0)
    setCanScrollTabsRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])
  useEffect(() => {
    checkTabScroll()
    const el = tabScrollRef.current
    if (!el) return
    el.addEventListener("scroll", checkTabScroll)
    window.addEventListener("resize", checkTabScroll)
    return () => { el.removeEventListener("scroll", checkTabScroll); window.removeEventListener("resize", checkTabScroll) }
  }, [checkTabScroll])

  return (
    <div className="paper_90 rounded-2xl overflow-hidden p-4">
      <div className="relative mb-6">
        {canScrollTabsLeft && (
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-10 flex items-center justify-start pl-1 bg-gradient-to-r from-paper to-transparent">
            <ChevronLeftIcon className="w-4 h-4 text-text_secondary" />
          </div>
        )}
        {canScrollTabsRight && (
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 z-10 flex items-center justify-end pr-1 bg-gradient-to-l from-paper to-transparent">
            <ChevronRightIcon className="w-4 h-4 text-text_secondary" />
          </div>
        )}
        <div ref={tabScrollRef} className="overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <Tabs
            variant="large"
            tabs={tabs}
            value={tab}
            onChange={handleTabChange}
            localUserPreferencesKey="account.billing.tab"
          />
        </div>
      </div>
      {tab === "payment-methods" && <UserPaymentMethods />}
      {tab === "invoices" && <InvoicesSection texts={texts} />}
      {tab === "billing-info" && <BillingDetailsCard />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Invoices Section
// ---------------------------------------------------------------------------
function InvoicesSection({ texts }) {
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [downloading, setDownloading] = useState(false)

  const getPaymentMethodInfo = (invoice) => {
    const paymentMethod = invoice.payment_intent?.payment_method
    if (!paymentMethod) return "N/A"
    if (invoice.payment_method_display) return invoice.payment_method_display
    if (paymentMethod.type === "card" && paymentMethod.card) {
      return `${paymentMethod.card.brand?.toUpperCase()} **** ${paymentMethod.card.last4}`
    }
    const methodDetails = paymentMethod[paymentMethod.type]
    if (!methodDetails) return paymentMethod.type?.toUpperCase() || "N/A"
    const parts = [paymentMethod.type.toUpperCase()]
    if (methodDetails.email) parts.push(`: ${methodDetails.email}`)
    else if (methodDetails.last4) parts.push(` **** ${methodDetails.last4}`)
    else if (methodDetails.bank_name) parts.push(`: ${methodDetails.bank_name}`)
    return parts.join("")
  }

  const handleDownload = (invoice) => {
    if (!invoice.invoice_pdf) return
    setDownloading(true)
    window.open(invoice.invoice_pdf, "_blank")
    setDownloading(false)
  }

  const handleView = (invoice) => {
    if (!invoice.hosted_invoice_url) return setSelectedInvoice(invoice)
    window.open(invoice.hosted_invoice_url, "_blank")
  }

  const columns = [
    {
      id: "created",
      title: texts?.invoiceDate,
      headerClassName: "w-[130px] pl-5 md:pl-4",
      itemClassName: "pl-5 md:pl-4",
      render: (value, item) => (
        <span className="text-sm text-text">
          {moment(item.created * 1000).format("MMM D, YYYY")}
        </span>
      ),
    },
    {
      id: "amount_paid",
      title: texts?.invoiceAmount,
      headerClassName: "w-[110px]",
      render: (value, item) => (
        <span className="text-sm text-text font-medium">
          {new Intl.NumberFormat("en-US", { style: "currency", currency: item.currency }).format(item.amount_paid / 100)}
        </span>
      ),
    },
    {
      id: "payment_method",
      title: texts?.invoicePaymentMethod,
      headerClassName: "w-[200px]",
      render: (value, item) => (
        <span className="text-sm text-text_secondary">
          {getPaymentMethodInfo(item)}
        </span>
      ),
    },
    {
      id: "status",
      title: texts?.invoiceStatus,
      headerClassName: "w-[100px]",
      render: (value, item) => (
        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
          item.status === "paid" ? "bg-success/10 text-success" : "bg-warning/10 text-warning"
        }`}>
          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
        </span>
      ),
    },
    {
      id: "actions",
      title: texts?.invoiceActions,
      headerClassName: "w-[100px] md:w-[80px]",
      render: (value, item) => (
        <div className="flex items-center gap-3">
          <Tooltip content={texts?.tooltipView}>
            <Button
              variant="icon"
              onClick={() => handleView(item)}
              disabled={!item.hosted_invoice_url}
            >
              <EyeIcon className="h-4 w-4" />
            </Button>
          </Tooltip>

          <Tooltip content={texts?.tooltipDownloadPDF}>
            <Button
              variant="icon"
              onClick={() => handleDownload(item)}
              disabled={!item.invoice_pdf || downloading}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ]

  return (
    <div className="bg-paper rounded-2xl border border-bordered overflow-hidden">
      <DataTable
        variant="pulse"
        preferredHeight="60vh"
        apiMethod={getInvoices}
        itemHeight="h-[52px]"
        headerControls={null}
        searchObject={{}}
        requestFields={{}}
        sort={{ created: -1 }}
        renders={columns}
        showFooter={true}
        apiDataField="data"
        minSearchTriggerTime={500}
        localUserPreferencesKey="account.invoices"
        onDataLoadingElement={
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        }
        noData={
          <div className="flex flex-col items-center justify-center min-h-[200px] gap-3">
            <DocumentTextIcon className="h-10 w-10 text-text_secondary/40" />

            <Typography variant="txt1_dim">
              {texts?.noInvoicesNote}
            </Typography>
          </div>
        }
      />

      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-paper rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6 pb-5 border-b border-bordered">
              <Typography 
                component="h3"
                variant="body1_accent" 
                className="my-0"
              >
                {texts?.invoiceDetails}
              </Typography>
              
              <Button 
                variant="icon"
                onClick={() => setSelectedInvoice(null)} 
                className="hover:text-text transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-4 text-sm">
              {[
                { label: texts?.invoiceDocNumber, value: selectedInvoice.number },
                { label: texts?.invoiceDetailDate, value: moment(selectedInvoice.created * 1000).format("MMM D, YYYY") },
                { label: texts?.invoiceDetailAmount, value: new Intl.NumberFormat("en-US", { style: "currency", currency: selectedInvoice.currency }).format(selectedInvoice.amount_paid / 100) },
                { label: texts?.invoiceDetailPaymentMethod, value: getPaymentMethodInfo(selectedInvoice) },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between">
                  <span className="text-text_secondary">
                    {label}
                  </span>

                  <span className="text-text font-medium">
                    {value}
                  </span>
                </div>
              ))}

              <div className="flex justify-between items-center">
                <span className="text-text_secondary">
                  {texts?.invoiceStatus}
                </span>

                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  selectedInvoice.status === "paid" ? "bg-success/10 text-success" : "bg-warning/10 text-warning"
                }`}>
                  {selectedInvoice.status.charAt(0).toUpperCase() + selectedInvoice.status.slice(1)}
                </span>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              {selectedInvoice.invoice_pdf && (
                <Button
                  variant="secondary"
                  onClick={() => handleDownload(selectedInvoice)}
                  className="flex-1 gap-2"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {texts?.invoiceDownloadButton}
                </Button>
              )}

              <Button
                variant="primary"
                onClick={() => setSelectedInvoice(null)}
                className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:opacity-90 transition-opacity"
              >
                {texts?.invoiceCloseButton}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Account Component
// ---------------------------------------------------------------------------
export default function Account() {
  // SSR guard — Account requires client-only APIs (Store, PlatformAuth)
  if (typeof window === "undefined") return null

  const texts = TextBundle({ id: "account" })
  const [auth] = PlatformAuth.useAuth()
  const [profile] = Value("user-profile")

  // Bridge /rt/account snap events to PubSub so the panels update live.
  useSnapRealtime()
  const logout = PlatformAuth.useLogout()
  const pageContext = usePageContext()
  const accountPath = pageContext?.urlPathname || (typeof window !== "undefined" ? window.location.pathname : "/account")
  const sectionParam = accountPath.match(/\/account\/([^/?#]+)/)?.[1] || null
  const subSectionParam = accountPath.match(/\/account\/[^/?#]+\/([^/?#]+)/)?.[1] || null

  const { data: menuData } = CMSData({
    contentType: "MenuItem",
    match: { "content.name": "AccountSitePanel" },
    itemsPerPage: 1,
  })

  const menuItems = menuData?.[0]?.items || []
  const isMenuEnabled = (item) => (item?.enabled ?? item?.content?.enabled) !== false
  const navItems = menuItems.filter(
    (item) => item.options?.action !== "logout" && isMenuEnabled(item)
  )
  const actionItems = menuItems.filter((item) => item.options?.action === "logout")

  const defaultSection = getSectionId(navItems[0]) || "profile"
  const validSections = new Set(navItems.map(getSectionId).filter(Boolean))
  // Trust the URL section as soon as it's present. CMSData loads the nav
  // menu asynchronously, so on first render `validSections` only contains
  // the synthetic Themes item; falling through to defaultSection here was
  // landing every direct hit on /account/my-files on the Themes panel
  // until the menu arrived.
  const activeSection = sectionParam || defaultSection

  const activeLabel = navItems.find((item) => getSectionId(item) === activeSection)?.label || ""

  const navScrollRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const checkNavScroll = useCallback(() => {
    const el = navScrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])
  useEffect(() => {
    checkNavScroll()
    const el = navScrollRef.current
    if (!el) return
    el.addEventListener("scroll", checkNavScroll)
    window.addEventListener("resize", checkNavScroll)
    return () => { el.removeEventListener("scroll", checkNavScroll); window.removeEventListener("resize", checkNavScroll) }
  }, [menuData, checkNavScroll])

  // Auth guard
  useEffect(() => {
    if (auth === null) {
      PubSub.publish("signin", { redirect: "/account" })
    }
  }, [auth])

  // If the user dismisses the sign-in dialog without completing it, this
  // page has nothing to show (auth stays null) — send them home instead
  // of leaving the spinner up forever.
  useEffect(() => {
    const token = PubSub.subscribe("signin-closed", () => {
      if (!auth) navigate("/")
    })
    return () => PubSub.unsubscribe(token)
  }, [auth])

  if (!auth) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spinner className="w-6 h-6" />
      </div>
    )
  }

  const handleSection = (sectionId) => {
    navigate(`/account/${sectionId}`)
  }

  const handleAction = (item) => {
    if (item.options?.action === "logout") {
      showPromptDialog({
        title: texts?.logOutTitle,
        message: texts?.logOutConfirmation,
        confirmText: texts?.logOutButton,
        cancelText: texts?.cancelButton,
        onConfirm: handleLogoutConfirm,
      })
    }
  }

  const handleLogoutConfirm = async () => {
    await logout()
    navigate("/")
  }

  return (
    <>
      <div className="fixed inset-0 pointer-events-none">
        <MainBackground />
      </div>
      {/* pt-24 clears the fixed h-16 site navbar */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 pt-24 pb-16">
      <div className="flex flex-col gap-6">
        {/* Top nav (horizontal on all sizes) */}
        <nav className="w-full flex justify-center">
          <div className="paper_20 rounded-2xl overflow-hidden w-fit max-w-full shadow-md ring-1 ring-white/5">
            {/* Horizontal scrollable nav */}
            <div className="relative">
              {canScrollLeft && (
                <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-10 flex items-center justify-start pl-1 bg-gradient-to-r from-paper to-transparent">
                  <ChevronLeftIcon className="w-4 h-4 text-text_secondary" />
                </div>
              )}
              {canScrollRight && (
                <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 z-10 flex items-center justify-end pr-1 bg-gradient-to-l from-paper to-transparent">
                  <ChevronRightIcon className="w-4 h-4 text-text_secondary" />
                </div>
              )}
              <div ref={navScrollRef} className="overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                <div className="p-2 flex flex-row flex-nowrap justify-start gap-2">
                  {navItems.map((item) => {
                    const Icon = ICON_MAP[item.name]
                    const sectionId = getSectionId(item)
                    const isActive = activeSection === sectionId
                    return (
                      <button
                        key={item.name}
                        onClick={() => handleSection(sectionId)}
                        className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl font-normal whitespace-nowrap transition-colors
                          ${isActive
                            ? "bg-paper text-primary shadow-sm"
                            : "text-text_secondary hover:text-text hover:bg-black/5"
                          }`}
                      >
                        {Icon && <Icon className="w-5 lg:w-7 h-5 lg:h-7 shrink-0" />}
                        <span className="text-xs md:text-sm lg:text-base whitespace-nowrap leading-tight">
                          {item.label}
                        </span>
                      </button>
                    )
                  })}
                  {actionItems.map((item) => {
                    const Icon = ICON_MAP[item.name]
                    return (
                      <button
                        key={item.name}
                        onClick={() => handleAction(item)}
                        className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl font-medium transition-colors text-text_secondary hover:text-text hover:bg-black/5"
                      >
                        {Icon && <Icon className="w-5 lg:w-7 h-5 lg:h-7 shrink-0" />}
                        <span className="text-xs md:text-sm lg:text-base  whitespace-nowrap leading-tight">{item.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {activeSection === "profile" && (
            <ProfileSection profile={profile} texts={texts} />
          )}
          {activeSection === "my-files" && <SnapFilesPanel />}
          {activeSection === "usage" && <SnapUsagePanel />}
          {activeSection === "licenses" && <LicensesPanel />}
          {activeSection === "downloads" && <DownloadsSection texts={texts} />}
          {activeSection === "plans" && <PlansSection texts={texts} />}
          {activeSection === "billing" && <BillingSection texts={texts} initialTab={subSectionParam || "payment-methods"} />}
        </div>
      </div>

      </div>
    </>
  )
}
